import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, LayoutIcon, SettingsIcon, SmartphoneIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Hero } from '../components/Hero';
import { PageCTA } from '../components/PageCTA';
import { ProblemSolution } from '../components/ProblemSolution';
import { SinglePlanOffer } from '../components/ui/SinglePlanOffer';
import { TestimonialsSection } from '../components/ui/TestimonialsSection';
import { projectExamples } from '../data/projects';
const services = [{
  icon: LayoutIcon,
  number: '01',
  title: 'Página web profesional',
  description: 'Una presencia clara y alineada con tu negocio para explicar qué haces y por qué importa.'
}, {
  icon: SmartphoneIcon,
  number: '02',
  title: 'Carta digital inteligente',
  description: 'Una experiencia móvil para presentar tu oferta sin PDFs, fricción ni información desactualizada.'
}, {
  icon: SettingsIcon,
  number: '03',
  title: 'Gestión incluida',
  description: 'Una base preparada para acompañarte después de publicar, con soporte y actualizaciones.'
}];
const principles = [['01', 'Claridad', 'Mensajes, jerarquía y acciones que el usuario entiende sin esfuerzo.'], ['02', 'Rendimiento', 'Una experiencia ligera, legible y adaptada a cada pantalla.'], ['03', 'Continuidad', 'Una presencia preparada para seguir siendo útil cuando tu negocio evoluciona.']];
export function HomePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="bg-black" data-id="element-363" data-mp="20pxg5vy0xids">
      <Hero data-id="element-364" data-mp="20t1wfrork4kt" />
      <ProblemSolution data-id="element-365" data-mp="25psayakx6s44" />

      <section className="section-shell border-t border-white/10 bg-[#0a0a0a]" aria-labelledby="system-title" data-id="element-366" data-mp="25q20jx21yx9a">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-367" data-mp="3c5m70yeef1jv">
          <div className="grid gap-8 border-b border-white/10 pb-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-368" data-mp="3h3w2zb7y666j">
            <div data-id="element-369" data-mp="2rfi90he1fcfg">
              <p className="eyebrow" data-id="element-370" data-mp="2reyrh1sauq1y">El sistema</p>
              <h2 id="system-title" className="mt-5 text-3xl font-medium tracking-[-0.045em] text-white sm:text-4xl" data-id="element-371" data-mp="2mi8dck0224j6">
                Las piezas adecuadas. Sin complejidad innecesaria.
              </h2>
            </div>
            <p className="max-w-2xl text-sm leading-relaxed text-neutral-300 sm:text-base" data-id="element-372" data-mp="2we1xopk0n2bi">
              Un sistema digital debe explicar, orientar y permanecer útil. Por
              eso conectamos web, carta y gestión bajo una misma lógica.
            </p>
          </div>
          <div className="grid border-l border-t border-white/10 md:grid-cols-3" data-id="element-373" data-mp="2ihg1rk3pnnjq">
            {services.map((service, index) => {
            const Icon = service.icon;
            return <motion.article key={service.number} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.15
            }} transition={{
              duration: 0.28,
              delay: index * 0.05,
              ease: [0.23, 1, 0.32, 1]
            }} className="group flex min-h-[250px] flex-col border-r border-b border-white/10 bg-black p-7 sm:p-8" data-id="element-374" data-mp="1py9b7ng93cli" data-mpi={index}>
                  <div className="flex items-start justify-between" data-id="element-375" data-mp="o5x40m1obzsq">
                    <Icon size={22} className="text-white" aria-hidden="true" data-id="element-376" data-mp="a7yq1hr9m5dr" />
                    <span className="meta-number" data-id="element-377" data-mp="e927g8oyo9h3">{service.number}</span>
                  </div>
                  <h3 className="mt-10 text-lg font-medium tracking-[-0.02em] text-white" data-id="element-378" data-mp="2uxo4hto23p5x">
                    {service.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-379" data-mp="2yxznl20rthfa">
                    {service.description}
                  </p>
                  <Link to="/servicios" className="mt-auto inline-flex w-fit items-center gap-2 pt-8 text-sm text-neutral-300 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-380" data-mp="3aggxtxypjoao">
                    Ver detalle <ArrowRightIcon size={15} aria-hidden="true" data-id="element-381" data-mp="216zn0xxv386" />
                  </Link>
                </motion.article>;
          })}
          </div>
        </div>
      </section>

      <SinglePlanOffer data-id="element-382" data-mp="3o7dpnmkt81go" />

      <section className="section-shell border-t border-white/10 bg-black" aria-labelledby="principles-title" data-id="element-383" data-mp="3o6kg6duguxuu">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-384" data-mp="3k8wfsva08cvk">
          <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-385" data-mp="2cujn4mqkkn9a">
            <div data-id="element-386" data-mp="28qbzytzrffd6">
              <p className="eyebrow" data-id="element-387" data-mp="28ptur7v4dqt4">El criterio</p>
              <h2 id="principles-title" className="mt-5 text-3xl font-medium tracking-[-0.045em] text-white sm:text-4xl" data-id="element-388" data-mp="o9w1celmjff1">
                Una experiencia pensada para facilitar decisiones.
              </h2>
            </div>
            <p className="max-w-2xl text-sm leading-relaxed text-neutral-300 sm:text-base" data-id="element-389" data-mp="x87mi13q1l03">
              No prometemos cifras sin contexto. Diseñamos los factores que
              ayudan a una presencia digital a ser más comprensible, confiable y
              accionable.
            </p>
          </div>
          <div className="mt-10 grid overflow-hidden border border-white/10 sm:grid-cols-3" data-id="element-390" data-mp="1g0in1oawl6v1">
            {principles.map(([number, title, detail]) => <div key={number} className="border-b border-white/10 p-7 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0" data-id="element-391" data-mp="1kybu0jk164wd">
                <p className="meta-number" data-id="element-392" data-mp="3ms19h0pffkvg">{number}</p>
                <h3 className="mt-10 text-lg font-medium text-white" data-id="element-393" data-mp="3mst4oaf43gl7">
                  {title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-394" data-mp="oxoho3zyzolu">
                  {detail}
                </p>
              </div>)}
          </div>
          <Link to="/resultados" className="link-arrow mt-8" data-id="element-395" data-mp="12v3cxpcwmqlq">
            Ver nuestro enfoque <ArrowRightIcon size={15} aria-hidden="true" data-id="element-396" data-mp="xwrz73jhnvy8" />
          </Link>
        </div>
      </section>

      <TestimonialsSection projects={projectExamples} data-id="element-397" data-mp="1ctizdf0zlbow" />
      <PageCTA data-id="element-398" data-mp="17x2d6qdcl7eg" />
    </div>;
}